import type { ReactNode } from 'react';
import type {
  SafetyFieldConfig,
  SafetySectionDefinition,
  SafetyFormSection,
} from '../config/schemas/types';
import type { DisplayFormatterType } from '@/components/blocks/forms';

/**
 * Represents a single item in a DataList for display purposes.
 */
export interface SafetyDisplayItem {
  label: string;
  value: ReactNode;
  mono?: boolean;
}

/**
 * A generated section ready for rendering.
 */
export interface SafetyGeneratedSection {
  key: string;
  title: string;
  items: SafetyDisplayItem[];
}

/**
 * Formatters for different value types in safety displays.
 */
export interface SafetyDisplayFormatters {
  text: (value: unknown) => string;
  number: (value: unknown, unit?: string) => string;
  boolean: (value: unknown) => string;
  array: (value: unknown) => string;
}

/**
 * Safely access nested object properties using dot notation.
 *
 * @example
 * ```ts
 * getNestedValue(unit, "technical.safety.detectors.toxicCount") // => 3
 * getNestedValue(unit, "technical.safety.ventilation.natural.highOpeningsAreaM2") // => 2.5
 * ```
 */
export function getNestedValue<T = unknown>(
  obj: Record<string, unknown> | null | undefined,
  path: string
): T | undefined {
  if (!obj || !path) return undefined;

  const keys = path.split('.');
  let current: unknown = obj;

  for (const key of keys) {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[key];
  }

  return current as T | undefined;
}

/**
 * Infers a display formatter from the field type when not explicitly specified.
 */
function inferFormatter(type: SafetyFieldConfig['type']): DisplayFormatterType {
  switch (type) {
    case 'date':
      return 'date';
    case 'month-year':
      return 'month-year';
    case 'checkbox':
      return 'boolean';
    case 'number':
      return 'number';
    default:
      return 'text';
  }
}

/**
 * Generates display sections for safety sub-tabs from schema.
 *
 * Unlike the base `generateDisplaySections`, this function:
 * - Supports `dataPath` for nested data access
 * - Groups by `panel` property instead of `section`
 * - Handles unit suffixes for numbers
 * - Formats arrays by joining with ", "
 * - Resolves catalog IDs to labels when catalogId and catalogLabelMaps are provided
 *
 * @example
 * ```tsx
 * const sections = generateSafetyDisplaySections(
 *   unit,
 *   rilevazioneFields,
 *   rilevazioneSections,
 *   defaultSafetyFormatters,
 *   { [CATALOG_IDS.PLANT_LOCATIONS]: locationLabelMap }
 * );
 * ```
 */
export function generateSafetyDisplaySections(
  entity: Record<string, unknown> | null | undefined,
  fields: readonly SafetyFieldConfig[],
  sectionDefs: Record<string, SafetySectionDefinition>,
  formatters: SafetyDisplayFormatters,
  catalogLabelMaps?: Record<string, Map<string, string>>
): SafetyGeneratedSection[] {
  // Group fields by panel
  const panelGroups = new Map<string, SafetyFieldConfig[]>();

  for (const field of fields) {
    const panel = field.panel ?? 'default';
    if (field.displayFormatter === 'skip') continue;

    const group = panelGroups.get(panel) ?? [];
    group.push(field);
    panelGroups.set(panel, group);
  }

  // Build sections with formatted values
  const sections: SafetyGeneratedSection[] = [];

  for (const [panelKey, panelFields] of panelGroups) {
    const sectionDef = sectionDefs[panelKey];
    if (!sectionDef) continue;

    const items: SafetyDisplayItem[] = [];

    for (const field of panelFields) {
      // Use dataPath if provided, otherwise fall back to key
      const path = field.dataPath ?? field.key;
      const rawValue = getNestedValue(entity, path);

      const formatterType: DisplayFormatterType =
        field.displayFormatter ?? inferFormatter(field.type);

      let formattedValue: string;

      if (Array.isArray(rawValue)) {
        // Arrays are joined with ", ", optionally resolving catalog IDs to labels
        if (field.catalogId && catalogLabelMaps?.[field.catalogId]) {
          const labelMap = catalogLabelMaps[field.catalogId];
          const resolvedValues = rawValue.map((id) =>
            typeof id === 'string' ? (labelMap.get(id) ?? id) : String(id)
          );
          formattedValue =
            resolvedValues.length > 0 ? resolvedValues.filter(Boolean).join(', ') || '—' : '—';
        } else {
          formattedValue = formatters.array(rawValue);
        }
      } else if (formatterType === 'boolean' || field.type === 'checkbox') {
        formattedValue = formatters.boolean(rawValue);
      } else if (field.type === 'number' || formatterType === 'number') {
        formattedValue = formatters.number(rawValue, field.unit);
      } else if (formatterType === 'select' && field.options) {
        // For select fields, map value to label using options
        const option = field.options.find((opt) => opt.value === rawValue);
        formattedValue = option?.label ?? formatters.text(rawValue);
      } else {
        formattedValue = formatters.text(rawValue);
      }

      items.push({
        label: field.label,
        value: formattedValue,
        mono: field.type === 'tel' || field.type === 'email',
      });
    }

    sections.push({
      key: panelKey,
      title: sectionDef.title,
      items,
    });
  }

  // Sort by order
  sections.sort((a, b) => {
    const orderA = sectionDefs[a.key]?.order ?? 999;
    const orderB = sectionDefs[b.key]?.order ?? 999;
    return orderA - orderB;
  });

  return sections;
}

/**
 * Default formatters for safety display sections.
 */
export const defaultSafetyFormatters: SafetyDisplayFormatters = {
  text: (value: unknown): string => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : '—';
    }
    return String(value);
  },

  number: (value: unknown, unit?: string): string => {
    if (value === null || value === undefined) return '—';
    if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
    return unit ? `${value} ${unit}` : String(value);
  },

  boolean: (value: unknown): string => {
    if (value === true) return 'Sì';
    if (value === false) return 'No';
    return '—';
  },

  array: (value: unknown): string => {
    if (!Array.isArray(value) || value.length === 0) return '—';
    return value.filter(Boolean).join(', ') || '—';
  },
};

/**
 * Generates form sections from safety field definitions.
 *
 * Groups fields by their `section` property and orders them
 * according to the section definitions.
 */
export function generateSafetyFormSections(
  fields: readonly SafetyFieldConfig[],
  sectionOrder: readonly string[],
  sectionTitles: Record<string, string>
): SafetyFormSection[] {
  // Group fields by section
  const groups = new Map<string, string[]>();

  for (const field of fields) {
    const section = field.section ?? 'default';
    const group = groups.get(section) ?? [];
    group.push(field.key);
    groups.set(section, group);
  }

  // Build form sections in order
  return sectionOrder
    .filter((key) => groups.has(key))
    .map((key) => ({
      title: sectionTitles[key] ?? key,
      fields: groups.get(key)!,
    }));
}
